import {
  siAnthropic,
  siArxiv,
  siBrave,
  siCloudflare,
  siGithub,
  siHuggingface,
  siLinear,
  siModelcontextprotocol,
  siNotion,
  siNpm,
  siPypi,
  siShopify,
  siStripe,
  siUpstash,
  siWikipedia,
  siYcombinator,
  type SimpleIcon,
} from 'simple-icons';
import { cn } from '@/lib/utils';

/** Logos shipped with the console (Simple Icons, CC0), keyed by slug. Nothing is fetched. */
const LOGOS: Record<string, SimpleIcon> = Object.fromEntries(
  [
    siAnthropic,
    siArxiv,
    siBrave,
    siCloudflare,
    siGithub,
    siHuggingface,
    siLinear,
    siModelcontextprotocol,
    siNotion,
    siNpm,
    siPypi,
    siShopify,
    siStripe,
    siUpstash,
    siWikipedia,
    siYcombinator,
  ].map((icon) => [icon.slug, icon]),
);

/** Near-black brand colours (GitHub, Notion…) would vanish in dark mode; use the text colour. */
function isDark(hex: string): boolean {
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  return 0.2126 * r! + 0.7152 * g! + 0.0722 * b! < 0.25;
}

function initials(name: string): string {
  const words = name.split(/[\s&-]+/).filter(Boolean);
  return (words.length > 1 ? words[0]![0]! + words[1]![0]! : name.slice(0, 2)).toUpperCase();
}

/**
 * A marketplace entry's logo: the brand mark when the console ships one, otherwise a monogram
 * on the brand colour.
 */
export function BrandMark({
  name,
  icon,
  brandColor,
  className,
}: {
  name: string;
  icon: string | null;
  brandColor: string | null;
  className?: string;
}) {
  const logo = icon ? LOGOS[icon] : undefined;
  if (logo) {
    return (
      <div
        className={cn(
          'bg-muted flex size-10 shrink-0 items-center justify-center rounded-lg border',
          className,
        )}
      >
        <svg
          viewBox="0 0 24 24"
          aria-hidden
          className="size-5"
          fill={isDark(logo.hex) ? 'currentColor' : `#${logo.hex}`}
        >
          <path d={logo.path} />
        </svg>
      </div>
    );
  }
  return (
    <div
      aria-hidden
      className={cn(
        'flex size-10 shrink-0 items-center justify-center rounded-lg border text-sm font-semibold text-white',
        !brandColor && 'bg-muted text-foreground',
        className,
      )}
      style={brandColor ? { backgroundColor: brandColor } : undefined}
    >
      {initials(name)}
    </div>
  );
}
